import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback } from "react";
import { TopBar } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Sparkles, ArrowRight } from "lucide-react";
import { MacroTab } from "@/components/thematic-maps/MacroTab";
import { SBDPanel } from "@/components/thematic-maps/SBDPanel";
import { SeedsTab } from "@/components/thematic-maps/SeedsTab";
import { NodesTab } from "@/components/thematic-maps/NodesTab";
import { KnowledgeTab } from "@/components/thematic-maps/KnowledgeTab";
import { ContentEnginePanel } from "@/components/thematic-maps/ContentEnginePanel";
import { SeoMetaPanel } from "@/components/thematic-maps/SeoMetaPanel";
import { SettingsExportTab } from "@/components/thematic-maps/SettingsExportTab";
import { AuditPanel } from "@/components/thematic-maps/AuditPanel";
import { QueueDashboard } from "@/components/thematic-maps/QueueDashboard";
import { PublishedQAPanel } from "@/components/thematic-maps/PublishedQAPanel";
import { IndexingPanel } from "@/components/thematic-maps/IndexingPanel";
import { WritingProfilePanel } from "@/components/thematic-maps/WritingProfilePanel";
import { ExistingPostsPanel } from "@/components/thematic-maps/ExistingPostsPanel";
import { SroStructurePanel } from "@/components/thematic-maps/SroStructurePanel";
import { MapPresentationView } from "@/components/thematic-maps/MapPresentationView";
import { UniquePagesPanel } from "@/components/thematic-maps/UniquePagesPanel";
import { AuthorInfoPanel } from "@/components/thematic-maps/AuthorInfoPanel";
import { BrandLogoPanel } from "@/components/thematic-maps/BrandLogoPanel";
import { MigrationPanel } from "@/components/thematic-maps/migration/MigrationPanel";
import { useStaticSite } from "@/hooks/useStaticSite";
import { useBackendImprovementStatus } from "@/hooks/useBackendImprovementStatus";
import { useContentAudits } from "@/hooks/useContentAudits";
import {
  PipelineProgress,
  PIPELINE_STEPS,
  PHASE_COLORS,
  findNextRecommendedStep,
} from "@/components/thematic-maps/PipelineProgress";
import { useThematicMaps } from "@/hooks/useThematicMaps";
import { useThematicSeeds } from "@/hooks/useThematicSeeds";
import { useThematicNodes } from "@/hooks/useThematicNodes";
import { useThematicEntities } from "@/hooks/useThematicEntities";
import { useThematicRelations } from "@/hooks/useThematicRelations";
import { useThematicNodeContent } from "@/hooks/useThematicNodeContent";
import { useThematicEntityAttributes } from "@/hooks/useThematicEntityAttributes";
import { useThematicWritingProfiles } from "@/hooks/useThematicWritingProfiles";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { uiLog } from "@/services/contentEngine/contentLogger";
import type { ThematicMap, MapStatus } from "@/types/thematicMaps";

const STATUS_LABELS: Record<MapStatus, string> = {
  draft: "Szkic",
  active: "Aktywna",
  archived: "Archiwum",
};

const ThematicMapDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getMap, updateMap } = useThematicMaps();
  const [map, setMap] = useState<ThematicMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState("macro");

  // Pipeline data hooks
  const { seeds } = useThematicSeeds(id);
  const { nodes } = useThematicNodes(id);
  const { entities } = useThematicEntities(id);
  const { relations } = useThematicRelations(id);
  const { contents } = useThematicNodeContent(id);
  const { attributes } = useThematicEntityAttributes(id);
  const { profiles, refreshProfiles } = useThematicWritingProfiles(id);
  const { audits: auditsMap, passedCount: auditPassedCount, warningCount: auditWarningCount } = useContentAudits(id || "");
  // Migracja WP -> strona statyczna (kropka w stepperze + status)
  const { site: staticSite } = useStaticSite({ mapId: id });

  useEffect(() => {
    if (id) {
      setLoading(true);
      getMap(id).then((data) => {
        setMap(data);
        setLoading(false);
      });
    }
  }, [id, getMap]);

  // Kulki Audyt/Macro/SBD/Profil czytają z `map` — bez tej subskrypcji panel
  // (np. AuditPanel) zapisuje audit_score do DB, a stepper widzi starą wartość do F5.
  useRealtimeSubscription({
    channel: `map_row_${id || "none"}`,
    table: "thematic_maps",
    filter: id ? `id=eq.${id}` : undefined,
    enabled: Boolean(id),
    onChange: () => {
      if (id) getMap(id).then((data) => { if (data) setMap(data); });
    },
  });

  const handleStatusChange = async (status: MapStatus) => {
    if (!id) return;
    const success = await updateMap(id, { status });
    if (success && map) setMap({ ...map, status });
  };

  const handleProfileChange = async (profileId: string) => {
    if (!id) return;
    const newId = profileId || null;
    const success = await updateMap(id, { active_profile_id: newId });
    if (success && map) setMap({ ...map, active_profile_id: newId });
  };

  const goToStep = useCallback((step: string) => {
    uiLog.public("PipelineNav", `Przejście do kroku: ${step}`);
    setActiveStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Faza "Ulepszanie zaplecza" — widoczna gdy flaga podstron lub author page włączone.
  // Liczone TU (przed completedSteps), bo status kropek tej fazy wchodzi do mapy „done".
  const activeProfileForAuthor = profiles.find((p) => p.id === map?.active_profile_id) || null;
  const backendImprovementEnabled =
    import.meta.env.VITE_UNIQUE_SUBPAGES_ENABLED === "true" ||
    Boolean(activeProfileForAuthor?.author_page_enabled);
  const { status: backendStatus, refresh: refreshBackendStatus } = useBackendImprovementStatus(
    id,
    backendImprovementEnabled,
    activeStep, // zmiana kroku = odświeżenie, więc zapisane logo/zdjęcia od razu widać
  );

  // Completed steps map
  const completedSteps: Record<string, boolean> = {
    macro: !!map?.macro_statement?.trim(),
    sbd: !!(map?.boundaries_owned && map.boundaries_owned.length > 0),
    seeds: seeds.length > 0,
    nodes: nodes.length > 0,
    entities: entities.length > 0,
    eav: attributes.length > 0,
    relations: relations.length > 0,
    "existing-posts": !!map?.wp_posts_synced_at,
    demand: nodes.filter((n: any) => n.demand_tier && n.demand_tier !== "unknown").length > 0,
    audit: map?.audit_score != null,
    profile: !!map?.active_profile_id,
    content: contents.filter((c) => c.content_status === "content_ready" || c.content_status === "approved").length > 0,
    "seo-meta": contents.filter((c) => (c as any).seo_title && (c as any).meta_description).length > 0,
    publish: contents.filter((c) => (c as any).engine_stage === "published").length > 0,
    // qa-check completed gdy każdy opublikowany ma audit z passed/warning (failed nie liczy)
    "qa-check": (() => {
      const publishedNodeIds = contents
        .filter((c) => (c as any).engine_stage === "published")
        .map((c) => c.node_id);
      if (publishedNodeIds.length === 0) return false;
      const goodAudits = publishedNodeIds.filter((nid) => {
        const a = auditsMap.get(nid);
        return a && (a.audit_status === "passed" || a.audit_status === "warning");
      });
      return goodAudits.length === publishedNodeIds.length;
    })(),
    // Indeksowanie done = każdy opublikowany ma indexed_at
    indexing: (() => {
      const pub = contents.filter((c) => (c as any).engine_stage === "published" && (c as any).wp_post_url);
      return pub.length > 0 && pub.every((c) => (c as any).indexed_at);
    })(),
    // STOP-H: 4 nowe sygnały dla SRO Structure phase
    "author-page": Boolean(
      profiles.find((p) => p.id === map?.active_profile_id)?.author_page_generated_at,
    ),
    pillar: nodes.some(
      (n: any) =>
        (n.document_role === "pillar" || n.node_role === "pillar") &&
        contents.find(
          (c) =>
            c.node_id === n.id &&
            ((c as any).engine_stage === "ready" ||
              (c as any).engine_stage === "published"),
        ),
    ),
    // Ulepszanie zaplecza — sygnały z useBackendImprovementStatus
    "unique-pages": backendStatus.uniquePagesReady > 0,
    "author-info": backendStatus.authorPhotos > 0 || backendStatus.personaFilled,
    "brand-logo": backendStatus.hasLogo,
    // Migracja done = domena przelaczona na strone statyczna
    migration: staticSite?.status === "live",
  };

  // STOP-H: hasSroEnabled — czy mapa ma jakakolwiek SRO flage true (per MF-6 z planu: filter mandatory)
  // Cluster Coverage + Entity Salience USUNIETE 2026-07-21 — zostaly Pillar + Strona autora.
  const hasSroEnabled = Boolean(
    map?.pillar_pages_enabled ||
    activeProfileForAuthor?.author_page_enabled,
  );
  // Faza "Ulepszanie zaplecza" wstawiana przed "presentation"
  // (backendImprovementEnabled + activeProfileForAuthor policzone wyżej, przed completedSteps).
  const visiblePhases = [
    "map", "knowledge", "existing", "validation", "profile", "content",
    ...(hasSroEnabled ? ["sro_structure"] : []),
    ...(backendImprovementEnabled ? ["backend_improvement"] : []),
    "presentation",
    // Migracja — tylko dla map z przypisana domena
    ...(map?.website_id ? ["migration"] : []),
  ];

  // Per-step summary (małe liczby pod każdym krokiem w stepperze)
  const readyContent = contents.filter(
    (c) => c.content_status === "content_ready" || c.content_status === "approved",
  ).length;
  const publishedContent = contents.filter((c) => (c as any).engine_stage === "published").length;
  const wpMatched = nodes.filter((n: any) => n.matched_wp_post_id).length;
  const stepSummary: Record<string, string | number | null> = {
    seeds: seeds.length || null,
    nodes: nodes.length || null,
    entities: entities.length || null,
    eav: attributes.length || null,
    relations: relations.length || null,
    "existing-posts": map?.wp_posts_count ? `${wpMatched}/${map.wp_posts_count}` : null,
    audit: map?.audit_score != null ? `${map.audit_score}/100` : null,
    content: contents.length ? `${readyContent}/${contents.length}` : null,
    "seo-meta": contents.length
      ? `${contents.filter((c) => (c as any).seo_title && (c as any).meta_description).length}/${contents.length}`
      : null,
    publish: contents.length ? `${publishedContent}/${contents.length}` : null,
    "qa-check": publishedContent > 0 ? `${auditPassedCount + auditWarningCount}/${publishedContent}` : null,
    indexing: publishedContent > 0
      ? `${contents.filter((c) => (c as any).engine_stage === "published" && (c as any).indexed_at).length}/${publishedContent}`
      : null,
    // Ulepszanie zaplecza — konkret zamiast "BRAK"
    "unique-pages": backendStatus.uniquePagesTotal
      ? `${backendStatus.uniquePagesReady}/${backendStatus.uniquePagesTotal}`
      : null,
    "author-info": backendStatus.authorPhotos
      ? `${backendStatus.authorPhotos} fot.`
      : backendStatus.personaFilled ? "dane" : null,
    "brand-logo": backendStatus.hasLogo ? "logo" : null,
    migration: staticSite
      ? ({ draft: "szkic", analyzed: "analiza", previewed: "podgląd", deployed: "pages.dev", live: "LIVE", rolled_back: "cofnięta", failed: "błąd" } as Record<string, string>)[staticSite.status] || null
      : null,
  };

  if (loading) {
    return (
      <>
        <TopBar title="Mapa Tematyczna" />
        <div className="flex-1 overflow-auto p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-10 w-full" />
        </div>
      </>
    );
  }

  if (!map) {
    return (
      <>
        <TopBar title="Mapa Tematyczna" />
        <div className="flex-1 overflow-auto p-6">
          <p className="text-muted-foreground">Mapa nie została znaleziona.</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate("/mapy-tematyczne")}>
            <ArrowLeft className="mr-2 h-4 w-4" /> Powrót do listy
          </Button>
        </div>
      </>
    );
  }

  // =============================================
  // Render panel for active step
  // =============================================
  const renderStepContent = () => {
    switch (activeStep) {
      case "macro":
        return (
          <MacroTab
            map={map}
            onSaved={(updates) => setMap((prev) => prev ? ({ ...prev, ...updates } as ThematicMap) : prev)}
            onNext={() => goToStep("sbd")}
          />
        );
      case "sbd":
        return <SBDPanel map={map} onNext={() => goToStep("seeds")} />;
      case "seeds":
        return <SeedsTab mapId={map.id} map={map} onNext={() => goToStep("nodes")} />;
      case "nodes":
        return <NodesTab mapId={map.id} map={map} onNext={() => goToStep("entities")} />;
      case "entities":
        return <KnowledgeTab map={map} activeSection="entities" />;
      case "eav":
        return <KnowledgeTab map={map} activeSection="eav" />;
      case "relations":
        return <KnowledgeTab map={map} activeSection="relations" />;
      case "existing-posts":
        return <ExistingPostsPanel mapId={map.id} mapName={map.name} />;
      case "audit":
        return (
          <div className="w-full space-y-6">
            <AuditPanel mapId={map.id} />
          </div>
        );
      case "profile":
        return <WritingProfilePanel mapId={map.id} mapName={map.name} />;
      case "content":
        return <ContentEnginePanel mapId={map.id} />;
      case "seo-meta":
        return <SeoMetaPanel mapId={map.id} />;
      case "publish":
        return <QueueDashboard mapId={map.id} websiteId={map.website_id} />;
      case "qa-check":
        return <PublishedQAPanel mapId={map.id} />;
      case "indexing":
        return <IndexingPanel map={map} />;
      // STOP-H: sekcje SRO Structure (Coverage + Salience usuniete 2026-07-21)
      case "pillar":
      case "author-page":
        return (
          <SroStructurePanel
            map={map}
            seeds={seeds}
            nodes={nodes as any}
            activeSection={activeStep as "pillar" | "author-page"}
            activeProfile={activeProfileForAuthor}
          />
        );
      case "unique-pages":
        return <UniquePagesPanel map={map} />;
      case "author-info":
        return <AuthorInfoPanel map={map} onProgress={refreshBackendStatus} />;
      case "brand-logo":
        return <BrandLogoPanel map={map} onProgress={refreshBackendStatus} />;
      case "migration":
        return <MigrationPanel map={map} />;
      case "presentation":
        return (
          <MapPresentationView
            map={map}
            seeds={seeds}
            nodes={nodes as any}
            contents={contents as any}
            activeProfile={activeProfileForAuthor}
          />
        );
      default:
        return <MacroTab map={map} onSaved={(updates) => setMap((prev) => prev ? ({ ...prev, ...updates } as ThematicMap) : prev)} />;
    }
  };

  return (
    <>
      <TopBar title="Mapa Tematyczna" />
      <div className="flex-1 overflow-auto p-6">
        {/* STOP-10 polish (2026-06-01): cap max-w-[1600px] cofnięty — flex-wrap kapsuły
            w PipelineProgress same się skalują, a cap dusił tabele content (np. Sprawdzanie). */}
        {/* Header */}
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate("/mapy-tematyczne")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0">
              <h2 className="text-lg font-semibold truncate">{map.name}</h2>
              <p className="text-sm text-muted-foreground">
                {map.map_version} · {map.language.toUpperCase()}
              </p>
            </div>
            {/* Sugerowany krok — obok nazwy mapy */}
            {(() => {
              const recommended = findNextRecommendedStep(PIPELINE_STEPS, completedSteps, activeStep);
              if (!recommended || recommended.key === activeStep) return null;
              const recPhase = PHASE_COLORS[recommended.phase];
              return (
                <button
                  type="button"
                  onClick={() => goToStep(recommended.key)}
                  className={`group hidden md:flex items-center gap-1.5 ml-2 px-3 h-9 rounded-md text-xs font-semibold transition-all border border-current ${recPhase.bg} ${recPhase.text} hover:shadow-sm hover:brightness-110`}
                  title={`Sugerowany nastepny krok: ${recommended.label}`}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span className="opacity-80">Sugerowany krok:</span>
                  <span className="font-bold">{recommended.label}</span>
                  <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                </button>
              );
            })()}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => goToStep("settings")}>
              Ustawienia
            </Button>
            <Select value={map.status} onValueChange={(v) => handleStatusChange(v as MapStatus)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">{STATUS_LABELS.draft}</SelectItem>
                <SelectItem value="active">{STATUS_LABELS.active}</SelectItem>
                <SelectItem value="archived">{STATUS_LABELS.archived}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Pipeline Progress — main navigation */}
        <PipelineProgress
          completedSteps={completedSteps}
          activeStep={activeStep}
          onStepClick={goToStep}
          summary={stepSummary}
          visiblePhases={visiblePhases}
        />

        {/* Active step content — keyed for fade-in transition on step change */}
        <div key={activeStep} className="min-h-[400px] animate-in fade-in slide-in-from-bottom-2 duration-300">
          {activeStep === "settings" ? (
            <SettingsExportTab map={map} onProfilesChanged={refreshProfiles} />
          ) : (
            renderStepContent()
          )}
        </div>
      </div>
    </>
  );
};

export default ThematicMapDetailPage;
